// Header engine light — lives in the TopBar next to "Assistant Online".
//
// Shows which engine drafts the action-item emails right now, as a red/green
// Claude-Code-CLI connection light:
//   🟢 CLI connected → drafts run $0 on the Claude subscription
//   🔴 CLI disconnected → paid Claude API (or no engine); click for the
//      connect guide + the ONE-CLICK enable button
//   ⚪ worker offline/disabled/probe-pending
//
// One-click enable (migration 0047): the button below flips the owner-writable
// app_settings.claude_cli_enabled row; the worker re-reads it every tick — no
// env editing, no restart. The only genuinely-local steps left in the guide
// are installing the CLI + signing in (they happen on the assistant's machine,
// not in a browser).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/lib/supabase';
import { useOwnerProfile } from '@/hooks/useOwnerProfile';
import { readHeartbeat, type HeartbeatRow } from '@reverra/shared/heartbeat/read';
import { describeDraftingEngine, type EngineLight } from '@/lib/draftingEngine';

const DAEMON = 'workers_action_item_email';
const CLI_TOGGLE_KEY = 'claude_cli_enabled';

const DOT: Record<EngineLight, string> = {
  green: 'bg-sage',
  red: 'bg-destructive',
  gray: 'bg-muted-foreground/50',
};

// The genuinely-local prerequisites (they run on the assistant's computer, so
// a browser can't do them) — everything else is the one-click button below.
const LOCAL_STEPS: Array<{ text: string; code?: string }> = [
  {
    text: 'On the computer running your Reverra assistant, open Terminal and install Claude Code:',
    code: 'npm install -g @anthropic-ai/claude-code',
  },
  {
    text: 'Run it once and sign in with your Claude subscription (Pro/Max):',
    code: 'claude   →  /login',
  },
];

async function writeCliToggle(next: boolean): Promise<void> {
  const { data, error } = await supabase
    .from('app_settings')
    .update({ value: next ? 'true' : 'false' })
    .eq('key', CLI_TOGGLE_KEY)
    .select('key');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('Toggle write affected zero rows (RLS or seed mismatch)');
  }
}

export function DraftingEngineChip(): JSX.Element | null {
  // Heartbeat rows are owner-readable only (migration 0025) — an admin/dev
  // session would just see a misleading permanent "offline", so render nothing
  // for non-owner roles (and while the role is still loading).
  const { role } = useOwnerProfile();
  const isOwner = role === 'owner';
  const qc = useQueryClient();

  const { data: row } = useQuery<HeartbeatRow | null>({
    queryKey: ['heartbeat', DAEMON],
    enabled: isOwner,
    queryFn: () => readHeartbeat(supabase, DAEMON),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const setCli = useMutation({
    mutationFn: writeCliToggle,
    onSuccess: (_res, next) => {
      toast.success(
        next
          ? 'Switching to the Claude Code CLI — the light turns green within a minute once it’s installed and signed in.'
          : 'Switched back to the Claude API.',
      );
      // The worker picks the toggle up on its next tick; nudge our poll too.
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['heartbeat', DAEMON] }), 8_000);
    },
    onError: () => toast.error('Could not update the engine setting.'),
  });

  const chip = describeDraftingEngine(row ?? null, new Date());

  if (!isOwner) return null;

  const cliSelected = chip.text.startsWith('Claude Code CLI');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary/60 px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          aria-label={`Drafting engine: ${chip.text}. Click for details.`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${DOT[chip.light]}`} aria-hidden="true" />
          <span>{chip.text}</span>
          <Info className="h-3 w-3 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 text-xs leading-relaxed">
        <p className="mb-1 font-medium text-foreground">AI drafting engine</p>
        <p className="text-muted-foreground">{chip.note}</p>

        {chip.showConnectSteps && (
          <div className="mt-3 space-y-2">
            <p className="font-medium text-foreground">Draft for $0 with the Claude Code CLI</p>
            <ol className="list-decimal space-y-1.5 pl-4 text-muted-foreground">
              {LOCAL_STEPS.map((s, i) => (
                <li key={i}>
                  {s.text}
                  {s.code && (
                    <code className="mt-0.5 block w-fit rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-foreground">
                      {s.code}
                    </code>
                  )}
                </li>
              ))}
              {!cliSelected && <li>Click the button below — no restart needed.</li>}
            </ol>
            {!cliSelected && (
              <Button
                size="sm"
                disabled={setCli.isPending}
                onClick={() => setCli.mutate(true)}
                className="mt-1"
              >
                <Zap className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Use Claude Code CLI ($0)
              </Button>
            )}
          </div>
        )}

        {/* The way BACK is always available while the CLI is selected — incl.
            the "not found"/"checking" states, so clicking enable before
            installing the CLI is never a drafting dead-end. */}
        {cliSelected && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              disabled={setCli.isPending}
              onClick={() => setCli.mutate(false)}
            >
              Switch to Claude API
            </Button>
          </div>
        )}

        <p className="mt-3 text-muted-foreground">
          Powers the action-item follow-up emails (sent from{' '}
          <span className="font-mono">reverra@agentmail.to</span>). Nothing sends without your
          explicit <span className="font-medium text-foreground">Send</span>.
        </p>
      </PopoverContent>
    </Popover>
  );
}
